import cn from 'classnames'
import React, {useEffect, useMemo, useRef} from 'react'
import {Button, ButtonType} from '@UI/components/controls/Button/Button.tsx'
import useShowHide from '../../../utils/useShowHide.ts'
import {Stack} from '../../Stack/Stack.tsx'
import {ArrowChevronDown} from '@UI/components/icons/ArrowChevronDown.tsx'
import {useTranslation} from 'react-i18next'
import styles from './FilterDropdown.module.less'
import {Gt} from "@src/i18n/Gt.tsx";

export type Option<T> = {
    label: React.ReactNode
    value: T
}

interface ItemWrapperProps<T> extends React.PropsWithChildren {
    option: Option<T>
}

interface Props<T> {
    options: Option<T>[]
    disabled?: boolean
    onChange?: (value: T) => void
    currentValue: string
    OptionWrapper?: React.FC<ItemWrapperProps<T>>
    buttonCssClass?: string
}

export function FilterDropdown<T = string>({
    OptionWrapper,
    currentValue,
    disabled,
    onChange,
    options,
    buttonCssClass
}: Props<T>) {
    const { t } = useTranslation()
    const dropdown = useShowHide(false)
    const selected = useMemo(() => options.find(x => x.value === currentValue)?.label, [options, currentValue])
    useEffect(() => dropdown.close(), [selected])

    const onClickOutside: EventListenerOrEventListenerObject = e => {
        if (e.target && !ref.current?.contains(e.target as Node)) {
            dropdown.close()
        }
    }
    useEffect(() => {
        if (dropdown.visible) {
            document.body.addEventListener('click', onClickOutside)
        }
        return () => document.body.removeEventListener('click', onClickOutside)
    }, [dropdown.visible])

    const Wrapper =
        OptionWrapper ||
        function ({ children }: React.PropsWithChildren) {
            return <>{children}</>
        }

    const ref = useRef<HTMLDivElement>(null)
    const filteredOptions = useMemo(() => options.filter(x => x.value !== currentValue), [options, currentValue])

    const selectOption = (value: T) => {
        dropdown.close()
        onChange && onChange(value)
    }

    return (
        <div ref={ref} className={cn(styles.root, { [styles.disabled]: disabled, [styles.opened]: dropdown.visible })}>
            <Button
                className={cn(styles.button, buttonCssClass, { [styles.buttonOpened]: dropdown.visible })}
                type={ButtonType.third}
                onClick={() => dropdown.toggle()}
            >
                <Stack justifyContent='space-between' alignItems='center'>
                    {selected}
                    <div className={cn(styles.iconWrapper, { [styles.iconInvert]: dropdown.visible })}>
                        <ArrowChevronDown />
                    </div>
                </Stack>
            </Button>

            <div className={cn(styles.dropdown, { [styles.dropdownVisible]: dropdown.visible })}>
                <div className={styles.dropdownList}>
                    {dropdown.visible && filteredOptions.map(option => (
                        <div
                            key={option.value as string}
                            className={styles.item}
                            onClick={() => selectOption(option.value)}
                        >
                            <Wrapper option={option}>
                                <div className={styles.linkInnerItem}>{option.label}</div>
                            </Wrapper>
                        </div>
                    ))}
                </div>
                {dropdown.visible && (
                    <Stack className={styles.cancelButtonBlock} justifyContent={"center"}>
                        <Button type={ButtonType.secondary} onClick={() => dropdown.close()}><Gt>Cancel</Gt></Button>
                    </Stack>
                )}
            </div>
        </div>
    )
}

